import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Input,
  LoadingSpinner,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { type PersonnelTeamMember } from "@/components/features/matter/information/MatterPersonnelTeamMembership";
import { getUserAccountStatus } from "@/lib/utils/user-account-status";
import { retrieveMatterTeammatesQueryOptions } from "@/lib/data/react-query/matters/matter-teammate";
import { type Matter, type MatterTeam } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { useQueries } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2, Users } from "lucide-react";

export const matterPersonnelTeamName = (
  matter: Matter,
  teams: MatterTeam[],
  id: string,
) =>
  teams.find((team) => team.id === id)?.label ??
  matter.teamConnections.find((team) => team.teamId === id)?.label ??
  "Team";

function TeamMembersPreview({
  teamName,
  members,
  loading = false,
  error = false,
}: {
  teamName: string;
  members: PersonnelTeamMember[];
  loading?: boolean;
  error?: boolean;
}) {
  const label = loading
    ? "Loading members"
    : error
      ? "Members unavailable"
      : `${members.length} member${members.length === 1 ? "" : "s"}`;
  return (
    <TooltipWrapper
      contentClassName="border border-border bg-popover text-popover-foreground shadow-md"
      message={
        loading ? (
          "Loading team members..."
        ) : error ? (
          "Team members could not be loaded."
        ) : members.length ? (
          <div className="max-h-64 w-72 max-w-[calc(100vw-2rem)] divide-y overflow-y-auto">
            {members.map((member) => (
              <div key={member.userId} className="py-1.5 leading-5">
                <div className="flex items-center gap-2 font-medium">
                  <span className="min-w-0 break-words">{member.name}</span>
                  {member.accountStatus && (
                    <Badge variant="muted" className="shrink-0">
                      {member.accountStatus}
                    </Badge>
                  )}
                </div>
                <div className="text-muted-foreground break-words">
                  {member.role}
                </div>
              </div>
            ))}
          </div>
        ) : (
          "No members"
        )
      }
    >
      <span
        className="text-muted-foreground inline-flex w-fit cursor-help items-center gap-1 text-xs"
        tabIndex={0}
        aria-label={`${teamName}: ${label}`}
      >
        {loading && <LoadingSpinner className="size-3" />}
        {label}
      </span>
    </TooltipWrapper>
  );
}

export function MatterPersonnelTeams({
  matter,
  teams,
  teamIds,
  membersByTeamId,
  canEditTeams,
  onEdit,
  onRequestRemoval,
}: {
  matter: Matter;
  teams: MatterTeam[];
  teamIds: string[];
  membersByTeamId: Map<string, PersonnelTeamMember[]>;
  canEditTeams: boolean;
  onEdit: () => void;
  onRequestRemoval: (teamId: string) => void;
}) {
  return (
    <section className="border-border overflow-hidden rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-medium">Teams</h3>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">
            {teamIds.length} assigned
          </span>
          {canEditTeams && (
            <Button
              variant={teamIds.length === 0 ? "outline" : "ghost"}
              size={teamIds.length === 0 ? "sm" : "icon"}
              className={teamIds.length === 0 ? "h-8 gap-1.5" : "size-8"}
              aria-label={teamIds.length === 0 ? "Add teams" : "Edit teams"}
              onClick={onEdit}
            >
              {teamIds.length === 0 ? (
                <>
                  <Plus className="size-4" aria-hidden="true" />
                  Add teams
                </>
              ) : (
                <Pencil className="size-4" />
              )}
            </Button>
          )}
        </div>
      </div>
      <div>
        {teamIds.length === 0 && (
          <p className="text-muted-foreground px-5 py-5 text-sm">
            No teams assigned.
          </p>
        )}
        {teamIds.map((id) => (
          <div
            key={id}
            className="border-border flex min-h-16 items-center gap-3 border-b px-5 py-3 last:border-b-0"
          >
            <Users className="text-muted-foreground size-5" />
            <span className="min-w-0 flex-1 text-sm">
              <span className="block truncate">
                {matterPersonnelTeamName(matter, teams, id)}
              </span>
              <TeamMembersPreview
                teamName={matterPersonnelTeamName(matter, teams, id)}
                members={membersByTeamId.get(id) ?? []}
              />
            </span>
            {canEditTeams && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${matterPersonnelTeamName(matter, teams, id)}`}
                onClick={() => {
                  onRequestRemoval(id);
                }}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export function MatterPersonnelTeamsPicker({
  teams,
  open,
  selectedIds,
  disabledUserIds,
  search,
  onSearchChange,
  onAdd,
  onRemove,
}: {
  teams: MatterTeam[];
  open: boolean;
  selectedIds: string[];
  disabledUserIds: Set<string>;
  search: string;
  onSearchChange: (search: string) => void;
  onAdd: (teamId: string) => void;
  onRemove: (teamId: string) => void;
}) {
  const visibleTeams = teams.filter((team) =>
    team.label.toLowerCase().includes(search.toLowerCase()),
  );
  const rosterQueries = useQueries({
    queries: visibleTeams.map((team) => ({
      ...retrieveMatterTeammatesQueryOptions({ id: team.id }),
      enabled: open,
    })),
  });
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-border border-b px-5 py-4">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            type="search"
            className="pl-9"
            aria-label="Search teams"
            placeholder="Search teams..."
            value={search}
            onChange={(event) => {
              onSearchChange(event.target.value);
            }}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        <p className="text-muted-foreground px-3 py-2 text-xs font-medium">
          Teams
        </p>
        {visibleTeams.map((team, index) => {
          const selected = selectedIds.includes(team.id);
          const rosterQuery = rosterQueries[index];
          const members: PersonnelTeamMember[] = [
            ...new Map(
              (rosterQuery.data?.data ?? []).map((member) => [
                member.userId,
                {
                  userId: member.userId,
                  name: formatUsersName(member) ?? "Unknown user",
                  role: member.roleLabel,
                  accountStatus: getUserAccountStatus(
                    member.status,
                    disabledUserIds.has(member.userId) ? false : undefined,
                  ),
                },
              ]),
            ).values(),
          ].sort((left, right) => left.name.localeCompare(right.name));
          return (
            <div
              key={team.id}
              className={`flex min-h-16 items-center gap-3 rounded-md px-3 py-2 ${selected ? "bg-muted" : "hover:bg-muted/60"}`}
            >
              <Users className="text-muted-foreground size-5 shrink-0" />
              <span className="min-w-0 flex-1 text-sm">
                <span className="block truncate">{team.label}</span>
                <TeamMembersPreview
                  teamName={team.label}
                  members={members}
                  loading={rosterQuery.isPending}
                  error={rosterQuery.isError}
                />
              </span>
              <Checkbox
                checked={selected}
                aria-label={`${selected ? "Remove" : "Add"} ${team.label}`}
                onCheckedChange={() => {
                  if (selected) onRemove(team.id);
                  else onAdd(team.id);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MatterPersonnelRemoveTeamDialog({
  matter,
  teams,
  teamId,
  canEditPeople,
  onClose,
  onRemove,
}: {
  matter: Matter;
  teams: MatterTeam[];
  teamId: string | null;
  canEditPeople: boolean;
  onClose: () => void;
  onRemove: (removeAssignees: boolean) => void;
}) {
  return (
    <AlertDialog
      open={teamId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent className="max-w-xl">
        <AlertDialogTitle>
          Remove{" "}
          {teamId ? matterPersonnelTeamName(matter, teams, teamId) : "team"}?
        </AlertDialogTitle>
        <AlertDialogDescription>
          {canEditPeople
            ? "Choose whether to keep the assignees added through this team. Changes are staged until you save Personnel."
            : "This change is staged until you save Personnel."}
        </AlertDialogDescription>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              onRemove(false);
            }}
          >
            Remove team only
          </Button>
          {canEditPeople && (
            <Button
              variant="destructive"
              onClick={() => {
                onRemove(true);
              }}
            >
              Remove team and its assignees
            </Button>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
